import type { LHCDetails, LhcItemDetail } from '@diamond/shared/types';
import {
  formatCurrencyIdr,
  formatShortDate,
  unixToHumanDate,
} from '@diamond/shared/utils';

import type { LHCDetailInvoiceRowData, LHCRowData } from '../types';
import {
  isMutationDisplayAnchor,
  resolveMutationDisplayAnchorIds,
  resolveMutationDisplayFields,
} from '../utils/lhc-mutation-group';
import {
  canFetchRelatedDeliveryOrders,
  resolveRelatedDeliveryOrderCount,
} from '../utils/lhc-related-delivery-orders';

export { formatCurrencyIdr as formatCurrency } from '@diamond/shared/utils';

export function mapLHCItemToRowData(
  item: LHCDetails,
  index: number,
  pageOffset: number
): LHCRowData {
  return {
    id: item.id,
    no: pageOffset + index + 1,
    lhcDate: formatShortDate(item.lhc_date),
    lhcNo: item.lhc_no || '-',
    plant: item.plant_name || '-',
    totalInvoice: item.total_invoice_count ?? 0,
    totalInvoiceValue: formatCurrencyIdr(item.total_invoice_amount),
    mutationValue: formatCurrencyIdr(item.total_mutation_amount),
    totalDifference: formatCurrencyIdr(item.total_difference_amount),
    lastUpdated: item.updated_at
      ? unixToHumanDate(item.updated_at, "d MMM yy '·' HH.mm")
      : '-',
    collectionPlantId: item.collection_plant_id,
    lhcDateRaw: item.lhc_date,
    updatedAtUnix: item.updated_at ?? null,
  };
}

function mapParentRow(
  item: LhcItemDetail,
  rowNumber: number,
  plantName: string,
  anchorIds: Set<string>
): LHCDetailInvoiceRowData {
  const isAnchor = isMutationDisplayAnchor(item, anchorIds);
  const relatedCount = resolveRelatedDeliveryOrderCount(item);
  const canExpandRelated = canFetchRelatedDeliveryOrders(item);
  const mutationFields = resolveMutationDisplayFields(item);

  return {
    rowId: item.id,
    parentId: '',
    orderMetaId: item.order_meta_id ?? null,
    deliveryOrderId: item.delivery_order_id ?? undefined,
    displayOrder: item.display_order,
    no: rowNumber,
    poNumber: item.po_number || '-',
    plant: plantName || '-',
    invoiceDate: item.invoice_date
      ? unixToHumanDate(item.invoice_date, 'd MMM yy')
      : '-',
    deliveryOrderNo: item.delivery_order_number || '-',
    invoiceNo: item.invoice_no || '-',
    billTo: item.sap_no_bilto ? String(item.sap_no_bilto) : '-',
    customerName: item.customer_name || '-',
    outletName: item.outlet_name || '-',
    poValue: formatCurrencyIdr(item.po_amount),
    soValue: formatCurrencyIdr(item.so_amount),
    invoiceValue: formatCurrencyIdr(item.invoice_amount),
    differenceAmount: mutationFields.differenceAmount,
    mutationValue: mutationFields.mutationValue,
    bankMutationNo: mutationFields.bankMutationNo,
    depositUsed: formatCurrencyIdr(item.deposit_used_amount),
    hasPaymentProof: (item.payment_proof_ids?.length ?? 0) > 0,
    paymentProofIds: item.payment_proof_ids ?? [],
    bankMutations: mutationFields.bankMutations,
    groupTotalInvoiceAmount: mutationFields.groupTotalInvoiceAmount,
    bank: item.bank || '-',
    remark: item.remark || '-',
    isChildRow: false,
    isMutationDisplayAnchor: isAnchor,
    showRelatedIndicator: isAnchor && relatedCount > 0 && canExpandRelated,
    hasChildren: isAnchor && relatedCount > 0 && canExpandRelated,
  };
}

export function mapInvoiceItemsToRows(
  items: LhcItemDetail[],
  plantName: string
): LHCDetailInvoiceRowData[] {
  const anchorIds = resolveMutationDisplayAnchorIds(items);

  return items.map((item, index) =>
    mapParentRow(item, index + 1, plantName, anchorIds)
  );
}
